import { useApiQuery } from '../../scripts/api/api_v2';
import { useEffect, useState } from 'react';
import { OrderPanel } from './OrderPanel';
import { Page } from '../../components/root/Page';
import { TixEvent, TixOrder, TixSale } from '../../types/TixTypes';
import { useSelectionStore } from '../../store/useSelectionStore';
import { ModalAddSale } from '../../components/modals/ModalAddSale';
import { ModalImport } from './ModalImport';
import { useEditOrderAPI } from '../../scripts/hooks/useEditOrderAPI';
import { ModalEditOrder, OnClickEditOrderModal } from '../../components/modals/ModalEditOrder';
import { ModalPatchSale } from '../../components/modals/ModalPatchSale';
import { GETOrdersResponse } from '../../types/TixAPITypes';
import { ModalImportRevoPayments } from './ModalImportRevoPayments';

export function OrdersPage() {
	const [addingSaleModal, setAddingSaleModal] = useState<TixEvent['id'] | undefined>(undefined);
	const [patchSaleModal, setPatchSaleModal] = useState<TixEvent['id'] | undefined>(undefined);
	const [importingData, setImportingData] = useState<boolean>(false);
	const [importRevoPayments, setImportRevoPayments] = useState<boolean>(false);

	const [editOrderModal, setEditOrderModal] = useState<Parameters<OnClickEditOrderModal>[0] | undefined>(undefined);

	// TODO: Add loading animation
	const { data, fetchData } = useApiQuery('tix.get-orders', {}, { enabled: true });

	// const { fetchData: deleteSeatsAPI } = useApiMutation('tix.delete-seats');

	const [cachedData, setCachedData] = useState<GETOrdersResponse | undefined>(undefined);
	const editOrderAPI = useEditOrderAPI({ updateState: setCachedData });
	const { selectedSeatIds, selectedEventId, clearSelection } = useSelectionStore();
	// const { setRemovingSeatsLoading } = useLoadingStore();
	// const deletingSeatsRef = useRef<boolean>(false);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		setCachedData(data);
	}, [data]);

	// NOTE: API Handlers
	// const removeSeatsAPI = async ({ eventId, seatIds }: { eventId: TixEvent['id']; seatIds: TixBought['id'][] }) => {
	// 	if (deletingSeatsRef.current) return;
	// 	setRemovingSeatsLoading(true);
	// 	deletingSeatsRef.current = true;

	// 	// NOTE: Make saving feel more realistic
	// 	await sleep(500);

	// 	try {
	// 		// NOTE: Send request and handle error
	// 		const res = await deleteSeatsAPI({
	// 			params: {
	// 				eventId: eventId,
	// 			},
	// 			body: {
	// 				seatIds: seatIds,
	// 			},
	// 		});
	// 		if (!res?.success) {
	// 			console.error(res?.error);
	// 			toast.error(res?.error ?? `Failed to remove seats for ${eventId}}`);
	// 			return;
	// 		}

	// 		setCachedData((state) => {
	// 			if (!state) return state;

	// 			const originalEventIndex = state.findIndex((event) => event.id === eventId);
	// 			for (const seatId of seatIds) {
	// 				delete state[originalEventIndex].seats[seatId];
	// 			}
	// 			return _.cloneDeep(state);
	// 		});
	// 		return true;
	// 	} catch (err) {
	// 		console.error(err);
	// 		toast.error(err?.toString() ?? 'Undefined error occured when trying to edit order');
	// 		return;
	// 	} finally {
	// 		setRemovingSeatsLoading(false);
	// 		deletingSeatsRef.current = false;
	// 	}
	// };

	// NOTE: Post API Handlers
	const onSaleAdded = (sale: TixSale) => {
		setCachedData((state) => {
			if (!state) return state;
			return state
				.map((event) => {
					if (event.id !== sale.eventId) return event;
					return {
						...event,
						seatIdsSold: [...event.seatIdsSold, ...sale.tixId],
						seatIdsUnsold: event.seatIdsUnsold.filter((seatId) => !sale.tixId.includes(seatId)),
					};
				})
				.filter((event) => Object.keys(event.seats).some((seatId) => event.seatIdsUnsold.includes(seatId)));
		});
		clearSelection();
	};

	const onOrderDeleted = (eventId: TixEvent['id'], orderId: TixOrder['id']) => {
		setCachedData((state) => {
			if (!state) return state;
			return state.map((event) => {
				if (event.id !== eventId) return event;
				delete event.orders[orderId];
				event.seats = Object.fromEntries(
					Object.entries(event.seats).filter((seat) => seat[1].orderId !== orderId),
				);
				return event;
			});
		});
	};

	// NOTE: Actions toolbar
	// const onClickDeleteSelectedSeats = async () => {
	// 	const selectedSeatIdsArray = Object.keys(selectedSeatIds);
	// 	if (!selectedEventId || selectedSeatIdsArray.length < 1) return;

	// 	const isSaved = await removeSeatsAPI({
	// 		eventId: selectedEventId,
	// 		seatIds: selectedSeatIdsArray,
	// 	});
	// 	if (isSaved) {
	// 		toast.success('Selected seats deleted!');
	// 		clearSelection();
	// 	}
	// };

	const nOrdersSelected = Object.entries(selectedSeatIds).filter((idPair) => idPair[1]);

	const pageActions = [
		{ text: 'New Sale', action: () => setAddingSaleModal(selectedEventId), disabled: nOrdersSelected.length < 1 },
		{ text: 'Add to Sale', action: () => setPatchSaleModal(selectedEventId), disabled: nOrdersSelected.length < 1 },
		// { text: 'Delete Seat', action: () => onClickDeleteSelectedSeats(), disabled: nOrdersSelected.length < 1 },
		{ text: 'Import Orders', action: () => setImportingData(true) },
	];

	// console.log(cachedData?.reduce((total, event) => {
	// 	return total + (event.stats?.totalValueToBeSold ?? 0);
	// }, 0));

	// 	console.log(cachedData?.reduce((total, event) => {
	// 	return total + (event.stats?.totalSoldAll ?? 0);
	// }, 0));

	return (
		<>
			{importingData && <ModalImport closeModal={() => setImportingData(false)} />}
			{importRevoPayments && <ModalImportRevoPayments closeModal={() => setImportRevoPayments(false)} />}
			{data && addingSaleModal && (
				<ModalAddSale
					event={data.find((event) => event.id === addingSaleModal)!}
					seatIds={nOrdersSelected.map((idPair) => idPair[0])}
					closeModal={() => setAddingSaleModal(undefined)}
					onSaleAdded={onSaleAdded}
				/>
			)}
			{data && patchSaleModal && (
				<ModalPatchSale
					event={data.find((event) => event.id === patchSaleModal)!}
					seatIds={nOrdersSelected.map((idPair) => idPair[0])}
					closeModal={() => setPatchSaleModal(undefined)}
					onSaleAdded={onSaleAdded}
				/>
			)}
			{editOrderModal && (
				<ModalEditOrder
					closeModal={() => setEditOrderModal(undefined)}
					editOrderAPI={editOrderAPI}
					onOrderDeleted={onOrderDeleted}
					orderId={editOrderModal.orderId}
					event={editOrderModal.event}
				/>
			)}
			<Page actions={pageActions}>
				{cachedData?.map((event) => {
					return (
						<OrderPanel
							event={event}
							editOrderAPI={editOrderAPI}
							key={event.id}
							onAddFine={setAddingSaleModal}
							onClickEditOrder={setEditOrderModal}
						/>
					);
				})}
			</Page>
		</>
	);
}
